// File handling utility functions

import {
  MAX_FILE_SIZE,
  FILE_TYPES,
  IMAGE_EXTENSION_FALLBACKS,
  MIME_BY_EXTENSION,
} from '../types';
import { convertHeicToJpeg, shouldConvertHeicToJpeg } from './heicConverter';

const truncateFileName = (fileName, maxLength = 30) => {
  if (fileName.length <= maxLength) {
    return fileName;
  }
  // Keep first part and last part with extension
  const extension = fileName.substring(fileName.lastIndexOf('.'));
  const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
  const maxNameLength = maxLength - extension.length - 3; // 3 for "..."

  if (nameWithoutExt.length <= maxNameLength) {
    return fileName;
  }

  const startLength = Math.floor(maxNameLength / 2);
  const endLength = maxNameLength - startLength;
  const start = nameWithoutExt.substring(0, startLength);
  const end = nameWithoutExt.substring(nameWithoutExt.length - endLength);

  return `${start}...${end}${extension}`;
};

const getFileExtension = (fileName) => {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex === -1) return '';
  return fileName.substring(dotIndex).toLowerCase();
};

const hasUnreliableMimeType = (mimeType) =>
  !mimeType || mimeType === 'application/octet-stream';

const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif']);

export const isHeicOrHeifImage = (type = '', name = '') => {
  const mime = (type || '').toLowerCase();
  if (HEIC_MIME_TYPES.has(mime)) return true;
  const ext = getFileExtension(name || '');
  return IMAGE_EXTENSION_FALLBACKS.includes(ext);
};

export const isImageAttachment = (attachment = {}) => {
  const type = attachment.type || '';
  const mediaKind = attachment.mediaKind || '';
  const name = attachment.name || '';

  if (type.startsWith('image/') || mediaKind === 'image' || mediaKind === 'sticker') {
    return true;
  }

  return isHeicOrHeifImage(type, name);
};

const isAppleMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const isDesktopSafari = () => {
  if (typeof navigator === 'undefined') return false;
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

/** HEIC/HEIF only renders natively in Safari and iOS browsers */
export const canBrowserPreviewImage = (type = '', name = '') => {
  if (!isHeicOrHeifImage(type, name)) return true;
  return isAppleMobileDevice() || isDesktopSafari();
};

export const isImageFile = (file) => {
  if (file.type && file.type.startsWith('image/')) return true;
  return IMAGE_EXTENSION_FALLBACKS.includes(getFileExtension(file.name));
};

export const isVideoFile = (file) =>
  Boolean(file.type && file.type.startsWith('video/'));

const getCanonicalMimeType = (file) => {
  const ext = getFileExtension(file.name);
  const extensionMime = MIME_BY_EXTENSION[ext];

  // Extension wins for HEIC/HEIF — browsers often report the wrong MIME (e.g. image/heif for .heic)
  if (extensionMime && HEIC_MIME_TYPES.has(extensionMime)) {
    return extensionMime;
  }

  if (!hasUnreliableMimeType(file.type)) {
    return file.type;
  }

  return extensionMime || file.type;
};

export const normalizeAttachmentFile = (file) => {
  const canonicalMime = getCanonicalMimeType(file);

  if (!canonicalMime || canonicalMime === file.type) {
    return file;
  }

  return new File([file], file.name, {
    type: canonicalMime,
    lastModified: file.lastModified,
  });
};

export const validateFile = (file) => {
  const errors = [];

  if (file.size > MAX_FILE_SIZE) {
    const maxSizeMB = MAX_FILE_SIZE / (1024 * 1024);
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const truncatedName = truncateFileName(file.name);
    errors.push(`File "${truncatedName}" is too large (${fileSizeMB}MB). Maximum allowed size is ${maxSizeMB}MB.`);
  }

  const isValidType = Object.values(FILE_TYPES).some((type) => {
    if (type === 'image/*') return isImageFile(file);
    if (type === 'video/*') return isVideoFile(file);
    return file.name.toLowerCase().endsWith(type.substring(1));
  });

  if (!isValidType) {
    const imageExtras = IMAGE_EXTENSION_FALLBACKS
      .map((ext) => ext.substring(1).toUpperCase())
      .join('/');
    const supportedTypes = Object.values(FILE_TYPES)
      .filter((type) => type !== 'image/*' && type !== 'video/*')
      .join(', ');
    const truncatedName = truncateFileName(file.name);
    const imageLabel = imageExtras ? `Images (${imageExtras})` : 'Images';
    errors.push(`File type not supported: "${truncatedName}". Supported types: ${imageLabel}, Videos, ${supportedTypes}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateFiles = (files) => {
  const validFiles = [];
  const errors = [];

  Array.from(files).forEach((file) => {
    const validation = validateFile(file);
    if (validation.isValid) {
      validFiles.push(normalizeAttachmentFile(file));
    } else {
      errors.push(...validation.errors);
    }
  });

  return {
    validFiles,
    errors,
  };
};

const validateConvertedFileSize = (file) => {
  if (file.size <= MAX_FILE_SIZE) {
    return null;
  }

  const maxSizeMB = MAX_FILE_SIZE / (1024 * 1024);
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
  const truncatedName = truncateFileName(file.name);
  return `Converted image "${truncatedName}" is too large (${fileSizeMB}MB). Maximum allowed size is ${maxSizeMB}MB.`;
};

/**
 * Normalize MIME types and convert HEIC/HEIF images to JPEG before upload.
 * @param {File} file
 * @returns {Promise<File>}
 */
export const prepareAttachmentForUpload = async (file) => {
  if (!file) return file;

  const normalized = normalizeAttachmentFile(file);
  if (!shouldConvertHeicToJpeg(normalized)) {
    return normalized;
  }

  const jpegFile = await convertHeicToJpeg(normalized);
  const sizeError = validateConvertedFileSize(jpegFile);
  if (sizeError) {
    throw new Error(sizeError);
  }

  return jpegFile;
};

/**
 * Validate files, then convert any HEIC/HEIF attachments to JPEG.
 * @param {FileList|File[]} files
 * @returns {Promise<{ validFiles: File[], errors: string[] }>}
 */
export const processFilesForUpload = async (files) => {
  const { validFiles, errors } = validateFiles(files);
  if (validFiles.length === 0) {
    return { validFiles, errors };
  }

  const preparedFiles = [];
  const conversionErrors = [];

  for (const file of validFiles) {
    try {
      preparedFiles.push(await prepareAttachmentForUpload(file));
    } catch (error) {
      const truncatedName = truncateFileName(file.name);
      conversionErrors.push(
        error?.message?.includes('too large')
          ? error.message
          : `Failed to convert "${truncatedName}" to JPEG. Please try another image or re-save it as JPG.`
      );
    }
  }

  return {
    validFiles: preparedFiles,
    errors: [...errors, ...conversionErrors],
  };
};

export const filesNeedHeicConversion = (files) =>
  Array.from(files).some((file) => shouldConvertHeicToJpeg(file));

export const createFilePreview = (file) => {
  return {
    name: file.name,
    size: file.size,
    type: file.type,
    url: URL.createObjectURL(file), // Create local URL for preview
  };
};

export const resolveMessageAttachmentMeta = (fileName, mediaKind = '', mediaMime = '') => {
  let type = mediaMime || '';
  let kind = mediaKind || '';

  if (!type) {
    if (kind === 'image' || kind === 'sticker') {
      type = 'image/jpeg';
    } else if (kind === 'video' || kind === 'gif') {
      type = 'video/mp4';
    } else if (kind === 'audio' || kind === 'voice') {
      type = 'audio/mpeg';
    } else if (isHeicOrHeifImage('', fileName)) {
      type = MIME_BY_EXTENSION[getFileExtension(fileName)] || 'image/heic';
    } else {
      type = 'application/octet-stream';
    }
  }

  if (!kind && (type.startsWith('image/') || isHeicOrHeifImage(type, fileName))) {
    kind = 'image';
  }

  return { type, mediaKind: kind };
};
